import vSelect from "vue-select";
import stickDropdownToSelect from '$utils/stickDropdownToSelect';

export default {
	props: {
		context: {
			type: Object,
			required: true
		},
		labelFormatter: {
			type: Function,
			default: (option) => option.label || option.name || option.title || ''
		}
	},

	components: {
		"v-select": vSelect,
	},

	computed: {
		selectOptions() {
			const options = this.context?.attrs?.options
				|| this.context?.node?.props?.options
				|| this.context?.options
				|| [];
			if (!Array.isArray(options)) return [];

			const seen = new Set();
			return options.reduce((list, option) => {
				const normalized = normalizeSelectOption(option);
				const key = String(normalized.value ?? '');
				if (key && seen.has(key)) return list;
				if (key) seen.add(key);
				list.push(normalized);
				return list;
			}, []);
		},
		selectClasses() {
			return [
				'formulate-input-element',
				`formulate-input-element--${this.context.type}`,
				{ 'formulate-input-element--empty': this.context._value === null || this.context._value === undefined || this.context._value === '' },
			];
		},
		selectedValue() {
			const value = this.context._value;
			if (value === null || value === undefined || value === '') {
				return null;
			}
			return value;
		},
	},

	methods: {
		optionIsAvailable(value) {
			if (value === null || value === undefined || value === '') {
				return false;
			}

			return this.selectOptions.some((option) => optionMatchesValue(option, value));
		},
		reduceOption(option) {
			return optionValue(option);
		},
		getOptionKey(option) {
			return String(optionValue(option) || optionLabel(option) || '');
		},
		calculateSelectPosition(dropdownList, component, { width }) {
			const trigger = component && (component.$el || (component.vnode && component.vnode.el));
			const inIncidentDetail = !!(trigger && trigger.closest && trigger.closest('.incidentDetail'));

			if (dropdownList && inIncidentDetail) {
				dropdownList.classList.add('vs__dropdown-menu--incident');
			}

			stickDropdownToSelect(dropdownList, component, {
				width,
				offsetY: inIncidentDetail ? -2 : 0,
			});
		},
		formatOptionLabel(option) {
			const resolved = this.resolveOption(option);
			const formatter = this.context?.attrs?.labelFormatter
				|| this.context?.node?.props?.labelFormatter
				|| this.context?.node?.props?.['label-formatter']
				|| this.labelFormatter;
			const formatted = typeof formatter === 'function'
				? formatter(resolved)
				: (resolved.label || '');
			return formatted || resolved.label || '';
		},
		resolveOption(option) {
			const unwrapped = unwrapSlotOption(option);
			if (unwrapped == null || unwrapped === '') {
				return { label: '', value: unwrapped };
			}

			const value = typeof unwrapped === 'object' ? optionValue(unwrapped) : unwrapped;
			const label = typeof unwrapped === 'object' ? optionLabel(unwrapped) : String(unwrapped);
			const found = this.selectOptions.find((item) => (
				optionMatchesValue(item, value)
				|| optionMatchesValue(item, label)
				|| optionMatchesValue(item, unwrapped)
			));

			if (found) return found;

			return typeof unwrapped === 'object'
				? normalizeSelectOption(unwrapped)
				: { label: String(unwrapped), value: unwrapped };
		},
		handleInput(value) {
			// FormKit Vue 3 compatibility
			if (this.context.node && typeof this.context.node.input === 'function') {
				this.context.node.input(value);
			} else if (this.context.handlers && typeof this.context.handlers.input === 'function') {
				this.context.handlers.input(value);
			}
		},
		onOptionSelect() {
			this.$emit('selected');
		},
		onClose: function () {
			if (this.context.handlers?.blur) {
				this.context.handlers.blur();
			} else if (this.context.blurHandler) {
				this.context.blurHandler();
			}
		},
	},
};

function unwrapSlotOption(option) {
	if (
		option
		&& typeof option === 'object'
		&& Object.prototype.hasOwnProperty.call(option, 'option')
		&& option.label == null
		&& option.value == null
		&& option.uuid == null
		&& option.id == null
	) {
		return option.option;
	}

	return option;
}

function optionValue(option) {
	if (option == null || typeof option !== 'object') return option;
	return option.value || option.uuid || option.id || option.machineName || '';
}

function optionLabel(option) {
	if (option == null) return '';
	if (typeof option !== 'object') return String(option);
	return option.label || option.name || option.title || option.text || '';
}

function normalizeSelectOption(option) {
	if (option == null || typeof option !== 'object') {
		return { label: option == null ? '' : String(option), value: option };
	}

	const value = optionValue(option);

	return {
		...option,
		label: optionLabel(option) || String(value || ''),
		value,
	};
}

function optionMatchesValue(option, value) {
	if (option == null || value == null || value === '') return false;
	if (typeof option !== 'object') return option === value;
	return option.value === value
		|| option.uuid === value
		|| option.id === value
		|| option.machineName === value;
}


